import { TeamName } from "@/components/TeamName";
import { AppointmentStartEndButton } from "@/features/appointments/AppointmentActionButtons";
import { AppointmentDetails } from "@/features/appointments/AppointmentDetails";
import { AppointmentStatusIndicator } from "@/features/appointments/AppointmentStatusIndicator";
import { useAppointmentActions } from "@/features/appointments/useAppointmentActions";
import { getAppointmentTitle } from "@/features/calendar/utils";
import { HistoryCreatedInfo } from "@/features/history/HistoryCreatedInfo";
import { getHistoryIcon } from "@/features/history/activityIcons";
import { useModals } from "@/features/modals/hooks";
import { type ModalDefinition } from "@/features/modals/types";
import {
  type ExtendedAppointment,
  type History,
  useOpportunity,
} from "@repo/api";
import {
  Badge,
  Button,
  ListView,
  ListViewItem,
  Loading,
  Tab,
  TabPanel,
  Tabs,
  Text,
  Variables,
  calendarIcon,
  checkmarkIcon,
  clockIcon,
  drillIcon,
  locationPinIcon,
  personIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  ElementsContainer,
  FlexDiv,
  ModalSheet,
  OneLineInfo,
  SectionContainer,
  translateAppointmentType,
  translateLocationType,
  translateServices,
  useCustomer,
} from "@repo/ui";
import {
  hasItems,
  isDefined,
  trackAnalytics,
  useTypesafeActions,
} from "@repo/utils";
import isEmpty from "lodash/isEmpty";

import { AppointmentEditButtons } from "./components/AppointmentEditButtons";
import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
  AppointmentModalStateReducer,
  appointmentModalStateActions,
} from "./components/AppointmentModalReducer";
import { AppointmentMoreActionButtons } from "./components/AppointmentMoreActionButtons";
import { AppointmentTime } from "./components/AppointmentTime";
import { DropInAppointmentEdit } from "./components/DropInAppointmentEdit";

interface AppointmentModalProps {
  isOpen: boolean;
  closeModal: () => void;
  appointment: ExtendedAppointment;
  historyEntries?: History[];
  showActions?: boolean;
  projectId?: number;
}

const getView = ({
  state,
  actions,
  onUpdate,
  onDelete,
  pushModal,
  closeModal,
  customer,
}: {
  state: AppointmentModalState;
  actions: AppointmentModalStateActions;
  onUpdate: () => void;
  onDelete: () => void;
  pushModal: (modal: ModalDefinition) => void;
  closeModal: () => void;
  customer?: Customer;
}) => {
  if (!state.appointment) {
    return { onBack: undefined, view: <Loading />, footer: undefined };
  }

  switch (state.step) {
    case "VIEW":
      return {
        onBack: undefined,
        footer: (
          <>
            <AppointmentStartEndButton appointment={state.appointment} />

            <Button
              text={t("label.more_actions")}
              size="small"
              type="secondary"
              onClick={() => {
                actions.setStep("MORE_ACTIONS");
                trackAnalytics("appointmnet_more_actions");
              }}
            />
          </>
        ),
        view: (
          <SectionContainer>
            <ElementsContainer>
              <FlexDiv
                justifyContent="space-between"
                gap={`${Variables.space50}`}
              >
                <Text textStyle="Heading.M">
                  {getAppointmentTitle(state.appointment)}
                </Text>

                <AppointmentEditButtons
                  state={state}
                  actions={actions}
                  onEdit={() => {
                    actions.setStep("EDIT");
                  }}
                  onDelete={() =>
                    pushModal({
                      type: "CONFIRM",
                      onConfirm: onDelete,
                      title: t("action.delete_appointment"),
                      message: t("prompt.confirm_delete_appointment"),
                    })
                  }
                />
              </FlexDiv>
              {state.appointment.appointmentType && (
                // Needs empty div for layout purposes
                <div>
                  <Badge
                    colour={
                      state.appointment.appointmentType === "WALKIN_BOOKING"
                        ? "extendedPalette19"
                        : "blue"
                    }
                    label={translateAppointmentType(
                      state.appointment.appointmentType
                    )}
                    size="small"
                  />
                </div>
              )}
              <ListView id="appointment-modal-details" showDivider={false}>
                <ListViewItem
                  leadingIcon={calendarIcon}
                  title={t("label.scheduled_time")}
                  description={
                    state.appointment.timeWindowStartTime ? (
                      <AppointmentTime
                        variant="MANAGE"
                        status={state.appointment.appointmentStatus}
                        startTime={state.appointment.timeWindowStartTime}
                        endTime={state.appointment.timeWindowEndTime}
                        customerTimezone={customer?.timezone}
                      />
                    ) : (
                      "-"
                    )
                  }
                />
                <ListViewItem
                  leadingIcon={clockIcon}
                  title={t("label.appointment_time")}
                  description={
                    state.appointment.actualTimeWindowStartTime ? (
                      <AppointmentTime
                        variant="MANAGE"
                        status={state.appointment.appointmentStatus}
                        startTime={state.appointment.actualTimeWindowStartTime}
                        endTime={state.appointment.actualTimeWindowEndTime}
                        customerTimezone={customer?.timezone}
                      />
                    ) : (
                      "-"
                    )
                  }
                />

                <ListViewItem
                  leadingIcon={locationPinIcon}
                  title={t("label.location")}
                  description={
                    <OneLineInfo
                      info={[
                        state.appointment.locationName,
                        translateLocationType(state.appointment.locationType),
                      ]}
                      divider=" - "
                    />
                  }
                />
                <ListViewItem
                  leadingIcon={drillIcon}
                  title={t("label.service")}
                  description={
                    translateServices(state.appointment.service) ?? "-"
                  }
                />
                <ListViewItem
                  leadingIcon={personIcon}
                  title={t("label.assigned_to")}
                  description={
                    !isEmpty(state.appointment.assignedCoworker) ? (
                      <CoWorkerName
                        email={state.appointment.assignedCoworker}
                      />
                    ) : state.appointment.assignedTeam ? (
                      <TeamName teamId={state.appointment.assignedTeam} />
                    ) : (
                      t("label.unassigned")
                    )
                  }
                />
                <ListViewItem
                  leadingIcon={checkmarkIcon}
                  title={t("label.status")}
                  description={
                    <AppointmentStatusIndicator
                      status={state.appointment.appointmentStatus}
                    />
                  }
                />
              </ListView>
              {state.appointment.videoLinkCoworker && (
                <Button
                  size="small"
                  href={state.appointment.videoLinkCoworker}
                  newWindow={true}
                  type="emphasised"
                  text={t("action.join_online_meeting")}
                />
              )}
            </ElementsContainer>

            <Tabs
              tabs={[
                hasItems(state.historyEntries) ? (
                  <Tab
                    key="appointment-histories-tab"
                    text={t("label.history")}
                    tabPanelId="appointment-histories"
                  />
                ) : null,
                <Tab
                  key="appointment-details-tab"
                  text={t("label.details")}
                  tabPanelId="appointment-details"
                />,
              ].filter(isDefined)}
              tabPanels={[
                <TabPanel
                  key="appointment-histories-tab"
                  tabPanelId="appointment-histories"
                >
                  <ListView id="appointment-history-entries">
                    {state.historyEntries?.map((history) => {
                      return (
                        <ListViewItem
                          key={history.id}
                          control="default"
                          title={history.title}
                          description={
                            <Text textStyle="Body.S">{history.note}</Text>
                          }
                          leadingIcon={getHistoryIcon(history.entryType)}
                          addOn={
                            <HistoryCreatedInfo
                              createdBy={history.createdBy}
                              createdAt={history.createdAt}
                            />
                          }
                        />
                      );
                    })}
                  </ListView>
                </TabPanel>,
                <TabPanel
                  key="appointmnet-details-tab"
                  tabPanelId="appointment-details"
                >
                  <AppointmentDetails appointment={state.appointment} />
                </TabPanel>,
              ]}
            />
          </SectionContainer>
        ),
      };
    case "EDIT":
      return {
        onBack: () => actions.setStep("VIEW"),
        footer: (
          <Button
            type="primary"
            text={t("action.save")}
            onClick={onUpdate}
            loading={state.loading === "UPDATING"}
          />
        ),
        view: <DropInAppointmentEdit state={state} actions={actions} />,
      };
    case "MORE_ACTIONS":
      return {
        onBack: () => actions.setStep("VIEW"),
        footer: undefined,
        view: (
          <AppointmentMoreActionButtons
            state={state}
            actions={actions}
            onClose={closeModal}
            customer={customer}
          />
        ),
      };
  }
};

export const AppointmentModal = ({
  isOpen,
  closeModal,
  appointment,
  historyEntries,
  showActions,
  projectId,
}: AppointmentModalProps) => {
  const { pushModal } = useModals();

  const { data: project } = useOpportunity(projectId);
  const { customer } = useCustomer(projectId, appointment.orderNumber, true);

  const [state, actions] = useTypesafeActions<
    AppointmentModalState,
    AppointmentModalStateActions
  >(
    AppointmentModalStateReducer,
    {
      step: showActions ? "MORE_ACTIONS" : "VIEW",
      appointment,
      historyEntries,
      opportunityId: projectId,
      loading: undefined,
      project,
      departments: project?.hfb,
      context: "PROJECT",
    },
    appointmentModalStateActions
  );

  const { onUpdate, onDelete } = useAppointmentActions(
    state,
    actions,
    closeModal
  );

  const view = getView({
    onUpdate,
    onDelete,
    state,
    actions,
    pushModal,
    closeModal,
    customer,
  });

  return (
    <ModalSheet
      title={t("label.appointment_with_number", {
        number: appointment.number,
      })}
      visible={isOpen}
      onClose={closeModal}
      onBack={view?.onBack}
      footer={view?.footer}
    >
      {view.view}
    </ModalSheet>
  );
};
